import { useState } from 'react';
import Layout from '../../../components/layout/Layout';
import { useNavigate } from 'react-router-dom';

export default function AuditoriaDashboardPage() {
  const navigate = useNavigate();

  const [periodo, setPeriodo] = useState('7dias');
  const [viewType, setViewType] = useState<'Diário' | 'Semanal' | 'Mensal'>('Semanal');

  const cards = [
    { title: 'Total de Vendas Recebidas', value: '1.247', icon: 'ri-shopping-cart-line', color: 'bg-blue-500', trend: '+12%', trendText: 'vs período anterior' },
    { title: 'Aprovadas Automaticamente', value: '892', icon: 'ri-checkbox-circle-line', color: 'bg-emerald-500', trend: '+8%', trendText: 'vs período anterior' },
    { title: 'Em Auditoria Manual', value: '234', icon: 'ri-time-line', color: 'bg-yellow-500', trend: '-5%', trendText: 'vs período anterior' },
    { title: 'Reprovadas', value: '89', icon: 'ri-close-circle-line', color: 'bg-red-500', trend: '+3%', trendText: 'vs período anterior' },
    { title: 'Pendentes de Documentação', value: '32', icon: 'ri-file-warning-line', color: 'bg-orange-500', trend: '-2%', trendText: 'vs período anterior' }
  ];

  // Dados do gráfico
  const chartData = [
    { day: 'Seg', aprovadas: 130, emAnalise: 25, reprovadas: 10 },
    { day: 'Ter', aprovadas: 155, emAnalise: 30, reprovadas: 8 },
    { day: 'Qua', aprovadas: 140, emAnalise: 35, reprovadas: 12 },
    { day: 'Qui', aprovadas: 165, emAnalise: 38, reprovadas: 15 },
    { day: 'Sex', aprovadas: 150, emAnalise: 40, reprovadas: 10 },
    { day: 'Sáb', aprovadas: 100, emAnalise: 25, reprovadas: 8 },
    { day: 'Dom', aprovadas: 90, emAnalise: 20, reprovadas: 5 },
  ];

  const maxValue = 180;

  // Função para criar curvas suaves (Bezier)
  const createSmoothPath = (values: number[]) => {
    const width = 800; // largura aproximada do gráfico
    const height = 300;
    const padding = 44; // padding left para os labels
    const bottomPadding = 30; // espaço para labels dos dias
    
    const chartWidth = width - padding;
    const chartHeight = height - bottomPadding;
    const stepX = chartWidth / (values.length - 1);
    
    // Converter valores para coordenadas Y
    const points = values.map((value, index) => ({
      x: padding + index * stepX,
      y: chartHeight - (value / maxValue) * chartHeight
    }));
    
    if (points.length === 0) return '';
    
    let path = `M ${points[0].x} ${points[0].y}`;
    
    // Criar curvas suaves usando Bezier
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      
      // Pontos de controle para curva suave
      const controlX = current.x + (next.x - current.x) / 2;
      
      path += ` C ${controlX} ${current.y}, ${controlX} ${next.y}, ${next.x} ${next.y}`;
    }
    
    return path;
  };

  const rankingVolume = [
    { posicao: 1, nome: 'Loja Centro - SP', cnpj: '12.345.678/0001-90', vendas: 342, aprovacao: '89%', badge: 'gold' },
    { posicao: 2, nome: 'Matriz São Paulo', cnpj: '23.456.789/0001-01', vendas: 298, aprovacao: '92%', badge: 'silver' },
    { posicao: 3, nome: 'Filial Campinas', cnpj: '34.567.890/0001-12', vendas: 276, aprovacao: '87%', badge: 'bronze' },
    { posicao: 4, nome: 'Loja Shopping ABC', cnpj: '45.678.901/0001-23', vendas: 245, aprovacao: '91%', badge: null },
    { posicao: 5, nome: 'PDV Zona Sul', cnpj: '56.789.012/0001-34', vendas: 223, aprovacao: '85%', badge: null }
  ];

  const rankingRejeicao = [
    { posicao: 1, nome: 'Loja Periferia A', cnpj: '67.890.123/0001-45', vendas: 87, rejeicao: '45%', badge: 'warning' },
    { posicao: 2, nome: 'Filial Norte', cnpj: '78.901.234/0001-56', vendas: 92, rejeicao: '38%', badge: 'warning' },
    { posicao: 3, nome: 'PDV Centro Oeste', cnpj: '89.012.345/0001-67', vendas: 78, rejeicao: '35%', badge: 'warning' },
    { posicao: 4, nome: 'Loja Express 24h', cnpj: '90.123.456/0001-78', vendas: 65, rejeicao: '32%', badge: null },
    { posicao: 5, nome: 'Franquia Litoral', cnpj: '01.234.567/0001-89', vendas: 54, rejeicao: '28%', badge: null }
  ];

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard de Auditoria</h1>
            <p className="text-sm text-gray-500 mt-1">Visão geral do status das vendas auditadas</p>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={periodo}
              onChange={(e) => setPeriodo(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-teal-500 focus:border-transparent cursor-pointer"
            >
              <option value="7dias">Últimos 7 dias</option>
              <option value="30dias">Últimos 30 dias</option>
              <option value="90dias">Últimos 90 dias</option>
            </select>
            <button
              onClick={() => window.print()}
              className="px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center space-x-2 font-medium"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-line"></i>
              </div>
              <span>Exportar Relatório</span>
            </button>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {cards.map((card, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-12 h-12 rounded-lg ${card.color} flex items-center justify-center shadow-sm`}>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${card.icon} text-white text-xl`}></i>
                  </div>
                </div>
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">{card.title}</p>
              <p className="text-2xl font-bold text-gray-900 mb-2">{card.value}</p>
              <div className="flex items-center space-x-1">
                <span className={`text-xs font-semibold ${card.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                  {card.trend}
                </span>
                <span className="text-xs text-gray-500">{card.trendText}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Gráfico de Evolução */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900">Evolução de Vendas Auditadas</h3>
            <div className="flex items-center space-x-2">
              {(['Diário', 'Semanal', 'Mensal'] as const).map((type) => (
                <button
                  key={type}
                  onClick={() => setViewType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    viewType === type
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-6 mb-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
              <span className="text-gray-600 font-medium">Aprovadas</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded-sm"></div>
              <span className="text-gray-600 font-medium">Em Auditoria</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
              <span className="text-gray-600 font-medium">Reprovadas</span>
            </div>
          </div>

          {/* Gráfico de Linhas */}
          <div className="relative" style={{ height: '300px' }}>
            {/* Grid horizontal */}
            <div className="absolute inset-0 flex flex-col justify-between">
              {[160, 120, 80, 40, 0].map((value, i) => (
                <div key={i} className="flex items-center">
                  <span className="text-xs text-gray-400 w-8 text-right mr-3">{value}</span>
                  <div className="flex-1 border-t border-gray-200"></div>
                </div>
              ))}
            </div>

            {/* Linhas do gráfico */}
            <svg className="absolute inset-0 w-full h-full">
              {/* Linha Verde - Aprovadas */}
              <path
                d={createSmoothPath(chartData.map(d => d.aprovadas))}
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
              />
              
              {/* Linha Amarela - Em Auditoria */}
              <path
                d={createSmoothPath(chartData.map(d => d.emAnalise))}
                fill="none"
                stroke="#f59e0b"
                strokeWidth="3"
              />
              
              {/* Linha Vermelha - Reprovadas */}
              <path
                d={createSmoothPath(chartData.map(d => d.reprovadas))}
                fill="none"
                stroke="#ef4444"
                strokeWidth="3"
              />
            </svg>

            {/* Labels dos dias */}
            <div className="absolute bottom-0 left-11 right-0 flex justify-between px-2">
              {chartData.map((data, index) => (
                <span key={index} className="text-sm font-medium text-gray-600">
                  {data.day}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Rankings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Ranking por Volume */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-trophy-line text-yellow-600 text-lg"></i>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900">PDVs com Maior Volume</h3>
              </div>
              <button
                onClick={() => navigate('/admin/auditoria/vendas')}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
              >
                Ver todos →
              </button>
            </div>
            <div className="space-y-3">
              {rankingVolume.map((pdv) => (
                <div
                  key={pdv.posicao}
                  className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => navigate('/admin/auditoria/vendas')}
                >
                  <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center font-bold text-white ${
                    pdv.badge === 'gold' ? 'bg-yellow-500' :
                    pdv.badge === 'silver' ? 'bg-gray-400' :
                    pdv.badge === 'bronze' ? 'bg-orange-600' :
                    'bg-gray-600'
                  }`}>
                    {pdv.posicao}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{pdv.nome}</p>
                    <p className="text-xs text-gray-500">{pdv.cnpj}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-sm">{pdv.vendas} vendas</p>
                    <p className="text-xs text-emerald-600 font-medium">{pdv.aprovacao} aprovação</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ranking por Rejeição */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-alert-line text-red-600 text-lg"></i>
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-900">PDVs com Maior Índice de Rejeição</h3>
              </div>
              <button
                onClick={() => navigate('/admin/auditoria/vendas')}
                className="text-sm text-teal-600 hover:text-teal-700 font-medium cursor-pointer whitespace-nowrap"
              >
                Ver todos →
              </button>
            </div>
            <div className="space-y-3">
              {rankingRejeicao.map((pdv) => (
                <div
                  key={pdv.posicao}
                  className="flex items-center space-x-4 p-4 bg-red-50 rounded-lg hover:bg-red-100 transition-colors cursor-pointer"
                  onClick={() => navigate('/admin/auditoria/vendas')}
                >
                  <div className="flex-shrink-0 w-9 h-9 bg-red-600 text-white rounded-lg flex items-center justify-center font-bold">
                    {pdv.posicao}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{pdv.nome}</p>
                    <p className="text-xs text-gray-500">{pdv.cnpj}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900 text-sm">{pdv.vendas} vendas</p>
                    <p className="text-xs text-red-600 font-medium">{pdv.rejeicao} rejeição</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
